import { useRef, useState } from 'react'
import { Upload, FileText, X } from 'lucide-react'
import { useUploadTaskFile } from '../../hooks'
import { DocumentTypeSelect } from './DocumentTypeSelect'

interface Props {
  taskId: number
}

const ACCEPT = 'image/*,application/pdf,application/msword,.docx,application/vnd.ms-excel,.xlsx'

/** Form uploadu pliku: drop-zone (klik lub drag&drop) + opcjonalna kategoria + Wgraj/Anuluj. */
export function FileUploadButton({ taskId }: Props) {
  const upload = useUploadTaskFile(taskId)
  const inputRef = useRef<HTMLInputElement>(null)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [docType, setDocType] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)

  const pickFile = (f: File | null | undefined) => {
    if (f) setPendingFile(f)
  }

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    pickFile(e.target.files?.[0])
  }

  const handleDrop = (e: React.DragEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setDragOver(false)
    pickFile(e.dataTransfer.files?.[0])
  }

  const submit = () => {
    if (!pendingFile) return
    upload.mutate(
      { file: pendingFile, documentTypeCode: docType ?? undefined },
      {
        onSuccess: () => {
          setPendingFile(null)
          setDocType(null)
          if (inputRef.current) inputRef.current.value = ''
        },
      },
    )
  }

  const cancel = () => {
    setPendingFile(null)
    setDocType(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  return (
    <div className="mt-3 flex flex-col gap-2">
      <input
        ref={inputRef}
        type="file"
        onChange={handleSelect}
        className="sr-only"
        accept={ACCEPT}
      />

      {!pendingFile && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`flex w-full items-center justify-center gap-2 rounded-md border border-dashed px-3 py-2 text-sm transition ${
            dragOver
              ? 'border-sky-500 bg-sky-50 text-sky-700'
              : 'border-slate-300 text-slate-600 hover:border-sky-400 hover:bg-sky-50/40 hover:text-sky-700'
          }`}
          title="Maks. 50 MB - PDF, DOC, XLS, obrazy"
        >
          <Upload className="h-4 w-4" />
          <span>Przeciągnij plik lub <span className="text-sky-600 underline">wybierz</span></span>
        </button>
      )}

      {pendingFile && (
        <div className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded bg-white text-slate-500">
              <FileText className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-slate-800">{pendingFile.name}</div>
              <div className="text-xs text-slate-500">{formatSize(pendingFile.size)}</div>
            </div>
            <button
              type="button"
              onClick={cancel}
              disabled={upload.isPending}
              className="flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:bg-white hover:text-slate-700 disabled:opacity-50"
              title="Wybierz inny plik"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <DocumentTypeSelect value={docType} onChange={setDocType} />

          <button
            type="button"
            onClick={submit}
            disabled={upload.isPending}
            className="flex items-center justify-center gap-1.5 rounded bg-sky-600 px-3 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50"
          >
            <Upload className="h-4 w-4" />
            {upload.isPending ? 'Wgrywanie…' : 'Wgraj plik'}
          </button>

          {upload.isError && (
            <p className="text-xs text-red-600">Błąd: nie udało się wgrać pliku.</p>
          )}
        </div>
      )}
    </div>
  )
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}
